<template>
  <div class="space-y-4 rounded-md bg-white pb-4 pl-4 pr-4 pt-1">
    <div>
      <div
        v-for="(_, i) in fields"
        :key="i"
        class="relative flex items-end border-b pb-3 pt-3 last:border-b-0 last:pb-0"
      >
        <div class="flex flex-1 gap-4">
          <component
            :is="
              field.type === 'multiselect'
                ? MultiSelectField
                : field.type !== 'select'
                  ? InputField
                  : SelectField
            "
            v-for="(field, j) in fieldSettings"
            :key="j"
            :name="field.name ? `${name}[${i}].${field.name}` : `${name}[${i}]`"
            :label="field.label"
            :adornment="field.adornment"
            :placeholder="field.placeholder"
            :type="field.type !== 'select' && field.type !== 'multiselect' ? field.type : undefined"
            :save-number-as-string="field.type === 'number' && field.saveNumberAsString"
            :min="field.type === 'number' ? field.min : undefined"
            :max="field.type === 'number' ? field.max : undefined"
            :max-fraction-digits="field.type === 'number' && field.maxFractionDigits"
            :options="
              field.type === 'select' || field.type === 'multiselect' ? field.options : undefined
            "
            class="flex-1"
          />
        </div>
        <button
          class="px-2 py-3"
          :class="{
            'text-bittersweet-600': fields.length > 1,
            'text-obsidian-400': fields.length === 1,
          }"
          :disabled="fields.length === 1"
          @click.prevent="removeItem(i)"
        >
          <XIcon class="size-4" />
        </button>
      </div>
    </div>
    <div v-if="!fields.length" class="flex justify-center p-4">
      {{ t('noEntries') }}
    </div>
    <InputHint :hint="error" is-error />
    <div class="flex justify-center">
      <CVButton variant="ghost" @click.prevent="addItem">
        {{ buttonText ?? t('add') }}
      </CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import InputField from '@/components/InputField.vue';
import SelectField from '@/components/SelectField.vue';
import MultiSelectField from '@/components/MultiSelectField.vue';
import { useI18n } from 'vue-i18n';
import { useFieldArray, useFieldError } from 'vee-validate';
import type { SelectOption } from '@/components/Select.vue';
import { XIcon } from 'lucide-vue-next';
import { Alert } from '@/services/alert.service';
import InputHint from '@/components/InputHint.vue';

export type FieldSettings = {
  name: string | null;
  label?: string;
  adornment?: string;
  placeholder?: string;
} & (
  | {
      type?: 'text';
    }
  | {
      type: 'number';
      saveNumberAsString?: boolean;
      min?: number;
      max?: number;
      maxFractionDigits?: number;
    }
  | {
      type: 'select';
      options: SelectOption[];
    }
  | {
      type: 'multiselect';
      options: SelectOption[];
    }
);

const { name, fieldSettings, showDeleteAlert } = defineProps<{
  buttonText?: string;
  name: string;
  fieldSettings: FieldSettings[];
  showDeleteAlert?: boolean;
}>();

const { t } = useI18n();

const { remove, push, fields } = useFieldArray<
  Record<string, number | string | null> | (number | string | null)
>(name);

const error = useFieldError(name);

const addItem = () => {
  if (fieldSettings[0].name === null) {
    return push(null);
  }
  const newItem: Record<string, number | string | null> = {};
  fieldSettings.forEach((field) => {
    newItem[field.name!] = null;
  });
  push(newItem);
};

const removeItem = async (i: number) => {
  if (showDeleteAlert) {
    const { isDenied } = await Alert.fire({
      text: t('reallyDeleteMessage'),
      icon: 'warning',
      showConfirmButton: false,
      showDenyButton: true,
      showCancelButton: true,
      denyButtonText: t('delete'),
      cancelButtonText: t('cancel'),
    });
    if (!isDenied) {
      return;
    }
  }
  remove(i);
};
</script>

<i18n lang="json">
{
  "en": {
    "noEntries": "No Entries",
    "add": "+ Add",
    "reallyDeleteMessage": "Do you really want to delete this item?"
  },
  "de": {
    "noEntries": "Keine Einträge",
    "add": "+ Hinzufügen",
    "reallyDeleteMessage": "Möchtest du diesen Eintrag wirklich löschen?"
  },
  "es": {
    "noEntries": "Sin entradas",
    "add": "+ Añadir",
    "reallyDeleteMessage": "¿Seguro que desea eliminar este elemento?"
  }
}
</i18n>
